import { treeUtil } from '@salescore/buff-common'
import type { ModelSearcher, ViewConfigSheet, ViewConfigTreeNode, ViewKpiAppearance } from '@salescore/core'
import { Array, pipe } from 'effect'
import { useMemo } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'

import { kpiFormAtom } from '../atoms'
import { useCurrentNodeProperties } from '../hooks/kpiForm'
import { useModelSearcher } from './modelSearcherSelector'

const defaultSheetConfig: ViewConfigSheet = {
  type: `sheet`,
}

export const useCurrentSheet = (): ViewConfigSheet => {
  const form = useRecoilValue(kpiFormAtom)
  const sheet = form.metricFormValue?.sheet
  return useMemo(() => sheet ?? defaultSheetConfig, [sheet])
}

export const useCurrentNodeModels = (tree: ViewConfigTreeNode | undefined) => {
  const nodes = tree === undefined ? [] : treeUtil.flatNodesWithParents(tree)
  const modelSearcher = useModelSearcher()
  return pipe(
    nodes,
    Array.flatMapNullishOr((node) => {
      const model = modelSearcher.searchModel(node.modelName)
      if (model === undefined) {
        return
      }
      // 「関連付けするオブジェクト」で表示するのにふさわしい、ノードのラベル名のパス
      return {
        node,
        model,
        nodePathAsLabel: [node, ...node.parents].map((n) => generateNodeLabel(n, modelSearcher)).toReversed(),
      }
    }),
  )
}

function generateNodeLabel(node: ViewConfigTreeNode, modelSearcher: ModelSearcher) {
  if (node.ui?.label !== undefined) {
    return node.ui.label
  }
  if (node.referenceToProperty === undefined) {
    const model = modelSearcher.searchModel(node.modelName)
    return model?.label ?? node.name
  }
  const x = modelSearcher.searchPropertyWithModel(
    node.referenceToProperty.modelName,
    node.referenceToProperty.propertyName,
  )
  if (x === undefined) {
    return node.name
  }
  // 親のノードに参照項目があるとき、参照項目の項目名を表示する
  if (node.name !== node.referenceToProperty.nodeName) {
    return x.property.label.replace(/ID/v, '')
  }
  // 自分のノードに参照項目があるとき、自分のモデル名とプロパティ名を表示する
  return `${x.model.label}(${x.property.label.replace(/ID/v, '')})`
}

type KpiFormCurrentNodeProperties = ReturnType<typeof useCurrentNodeProperties>
export type KpiFormCurrentNodeProperty = KpiFormCurrentNodeProperties[0]

export const useCurrentNodeDateProperties = () => {
  const properties = useCurrentNodeProperties()
  return properties.filter(({ property }) => property.type === 'date' || property.type === 'datetime')
}

export const useCurrentNodeStringProperties = () => {
  const properties = useCurrentNodeProperties()
  return properties.filter(({ property }) => property.type === 'string')
}

export const useKpi = () => {
  const [form, setForm] = useRecoilState(kpiFormAtom)
  const kpi: Partial<ViewKpiAppearance> = form.ui?.kpi ?? {}
  const setKpi = (kpi: Partial<ViewKpiAppearance>) => {
    setForm((oldValue) => ({
      ...oldValue,
      ui: {
        ...oldValue.ui,
        kpi: {
          ...oldValue.ui?.kpi,
          ...kpi,
        },
      },
    }))
  }
  return { kpi, setKpi }
}
